import React from 'react';

import { QuickSearchEntity } from '@lumapps/quick-search/types';
import {
    addInteractionSuggestion,
    removeInteractionSuggestion,
    removeSearchHistorySuggestion,
} from '@lumapps/quick-search/utils/suggestionsUtils';
import { MAX_QUERY_LENGTH } from '@lumapps/search/constants/query';
import { SearchSuggestion, SearchSuggestionType } from '@lumapps/search/types';
import { isSearchQueryValid } from '@lumapps/search/utils';
import { getDateNowISO } from '@lumapps/utils/date/getDateNowISO';

export interface UseSearchBoxOptions {
    /** Current query value (controlled from outside) */
    query: string;
    /** Whether user is connected */
    isUserConnected: boolean;
    /** Instance ID for suggestions storage */
    instanceId: string;
    /** Callback when search should be triggered */
    onSearchTrigger: (query: string) => void;
    /** Callback when query changes */
    onQueryChange: (query: string) => void;
    /** Callback when focus state changes */
    onFocusChange: (isFocus: boolean) => void;
    /** Whether to scroll to top on search */
    scrollToTopOnSearch?: boolean;
    /** Custom interaction handler (for storing quick search interactions) */
    addQuickSearchInteraction?: (item: QuickSearchEntity, query: string, connected: boolean) => void;
}

export interface UseSearchBoxReturn {
    /** Handler for query changes */
    handleQueryChange: (newQuery: string) => void;
    /** Handler for deleting a suggestion from history or interactions */
    handleDeleteSuggestion: (suggestion: SearchSuggestion) => void;
    /** Handler for when a suggestion is selected */
    handleSuggestionSelected: (suggestion: SearchSuggestion) => void;
    /** Trigger a search with the given query */
    triggerSearchQuery: (query: string) => void;
}

/**
 * Common handlers for SearchBox functionality.
 * Extracted to avoid duplication between search-box and search-segmented implementations.
 *
 * This hook provides:
 * - Query change handling with MAX_QUERY_LENGTH limit
 * - Suggestion deletion (history and interactions)
 * - Suggestion selection logic (interactions vs search triggers)
 * - Search triggering with validation and optional scroll-to-top
 */
export const useSearchBox = ({
    query,
    isUserConnected,
    instanceId,
    onSearchTrigger,
    onQueryChange,
    onFocusChange,
    scrollToTopOnSearch = true,
    addQuickSearchInteraction = addInteractionSuggestion,
}: UseSearchBoxOptions): UseSearchBoxReturn => {
    // Query change with length limit
    const handleQueryChange = React.useCallback(
        (newQuery: string) => {
            const limitedValue = newQuery.length > MAX_QUERY_LENGTH ? newQuery.slice(0, MAX_QUERY_LENGTH) : newQuery;
            onQueryChange(limitedValue);
        },
        [onQueryChange],
    );

    //  Delete suggestion from history or interactions
    const handleDeleteSuggestion = React.useCallback(
        (suggestion: SearchSuggestion) => {
            switch (suggestion.type) {
                case SearchSuggestionType.HISTORY:
                    removeSearchHistorySuggestion(
                        {
                            query: suggestion.query,
                            siteId: instanceId,
                        },
                        isUserConnected,
                    );
                    break;
                case SearchSuggestionType.INTERACTION:
                    removeInteractionSuggestion(
                        {
                            ...((suggestion.item as QuickSearchEntity) || {}),
                            siteId: instanceId,
                        },
                        isUserConnected,
                    );
                    break;
                default:
                    break;
            }
        },
        [instanceId, isUserConnected],
    );

    // Trigger search with validation and optional scroll
    const triggerSearchQuery = React.useCallback(
        (query: string) => {
            if (isSearchQueryValid(query)) {
                onSearchTrigger(query);
                onFocusChange(false);

                if (scrollToTopOnSearch) {
                    window.scrollTo(0, 0);
                }
            }
        },
        [onSearchTrigger, onFocusChange, scrollToTopOnSearch],
    );

    // Suggestion selected
    const handleSuggestionSelected = React.useCallback(
        (suggestion: SearchSuggestion) => {
            // INTERACTION type: store the interaction and close dropdown
            if (SearchSuggestionType.INTERACTION === suggestion.type) {
                if (suggestion.item) {
                    const { item } = suggestion;

                    // Store the item with updated lastUsed timestamp
                    addQuickSearchInteraction(
                        {
                            ...item,
                            originSiteId: item.originSiteId || item.siteId,
                            lastUsed: getDateNowISO(),
                        },
                        query,
                        isUserConnected,
                    );
                }
                onFocusChange(false);
                return;
            }

            // HISTORY or SUGGESTION without item: trigger search
            if (
                suggestion.type === SearchSuggestionType.HISTORY ||
                /**
                 * If a suggestion has an item, the option will redirect to the item
                 * itself and we don't want to trigger a search.
                 * So we only trigger search if no item is set.
                 * */
                (suggestion.type === SearchSuggestionType.SUGGESTION && !suggestion.item) ||
                !suggestion.type
            ) {
                triggerSearchQuery(suggestion.query);
            }
        },
        [query, isUserConnected, addQuickSearchInteraction, onFocusChange, triggerSearchQuery],
    );

    return {
        handleQueryChange,
        handleDeleteSuggestion,
        handleSuggestionSelected,
        triggerSearchQuery,
    };
};
